<template>
  <div class="mb-3">
    <label v-if="label" class="form-label">
      {{ label }}<span v-if="required" class="text-red"> *</span>
      <EmailRoleBadge :role="emailRole" />
    </label>

    <Popover
      placement="bottom-start"
      width="340px"
      panel-class="tw:max-h-[22rem] tw:overflow-y-auto tw:p-3"
      @open="ensureCompanyImagesLoaded">
      <template #trigger>
        <div class="row align-items-end image-option-trigger" role="button">
          <div class="col-5">
            <div v-if="isLoading" class="avatar w-100 avatar-upload rounded" style="height: 7rem">
              <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
            </div>
            <AdImagePicker
              v-else-if="!modelValue?.hash"
              :uploading="isUploading"
              :width="'100%'"
              :height="112"
              :uploadText="$t('campaigns.choose_image')"
              @fileDrop="handleFileDrop" />
            <div v-else class="card">
              <div class="finder-object-image-wrapper">
                <img v-if="previewUrl" :src="previewUrl" alt="" />
                <div v-else class="avatar w-100 rounded" style="height: 7rem">
                  <i class="ti ti-photo-off icon"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="col">
            <small v-if="description" class="form-text text-muted d-block mb-2">{{ description }}</small>
            <template v-if="modelValue?.hash">
              <div class="mb-2">
                <code class="d-block text-truncate">{{ modelValue.hash }}</code>
              </div>
              <button type="button" class="btn btn-white btn-sm me-2">
                {{ $t('publication.editor.image.change') }}
              </button>
              <button type="button" class="btn btn-white btn-sm" @click.stop.prevent="clearImage">
                {{ $t('publication.editor.image.clear') }} <i class="ti ti-trash"></i>
              </button>
            </template>
          </div>
        </div>
      </template>

      <template #dropdown="{ closeDropdown: close }">
        <div class="image-picker-content">
          <div v-if="companyImagesLoading" class="text-center text-muted py-3">
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </div>
          <template v-else>
            <template v-if="companyImages.length">
              <div class="image-picker-heading text-muted mb-2">
                {{ $t('publication.editor.image.company_images') }}
              </div>
              <div class="image-picker-grid mb-2">
                <button
                  v-for="img in companyImages"
                  :key="img.hash"
                  type="button"
                  class="image-picker-thumb"
                  :class="{ selected: img.hash === modelValue?.hash }"
                  :title="img.hash"
                  @click="
                    selectCompanyImage(img);
                    close();
                  ">
                  <img :src="img.url" alt="" />
                </button>
              </div>
            </template>
            <div v-else class="text-muted small mb-2">{{ $t('publication.editor.image.no_company_images') }}</div>

            <div class="dropdown-divider"></div>
            <button
              type="button"
              class="btn btn-white btn-sm w-100"
              @click="
                close();
                selectImage();
              ">
              <i class="ti ti-upload me-1"></i> {{ $t('publication.editor.image.upload') }}
            </button>
          </template>
        </div>
      </template>
    </Popover>

    <input ref="fileInput" type="file" accept=".jpg,.jpeg,.png,.gif,.webp" class="d-none" @change="onFileSelected" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { fetchImageMeta, fetchCompanyImages } from '@/api/company';
import { requestMultipart, ClientError } from '@/api/client';
import type { ImageObject } from '@/types/types';
import type { ImageOption } from '@prospective/pops-shared';
import AdImagePicker from '@/components/form/AdImagePicker.vue';
import Popover from '@/components/common/Popover.vue';
import EmailRoleBadge from '@/components/orders/creation/subcomponents/EmailRoleBadge.vue';
import translator from '@/locale/translator';

interface CompanyImageThumb {
  hash: string;
  url: string;
  focus?: { x: number; y: number };
}

const props = defineProps<{
  modelValue: ImageOption | null;
  label?: string;
  description?: string;
  required?: boolean;
  emailRole?: string;
  companyId?: number;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: ImageOption | null];
}>();

const toast = useToast();
const fileInput = ref<HTMLInputElement | null>(null);

// preview url for the selected hash; set on upload and fetched for saved hashes.
const previewUrl = ref('');
const isLoading = ref(false);
const isUploading = ref(false);
let loadedHash = '';

// loaded lazily the first time the popover opens.
const companyImages = ref<CompanyImageThumb[]>([]);
const companyImagesLoading = ref(false);
let companyImagesLoaded = false;

const currentHash = computed(() => props.modelValue?.hash ?? '');

async function loadPreview(hash: string) {
  if (!hash || hash === loadedHash) return;
  isLoading.value = true;
  try {
    const response = await fetchImageMeta(hash);
    previewUrl.value = response.data?.proxy?.url_preview ?? '';
    loadedHash = hash;
  } catch {
    previewUrl.value = '';
  } finally {
    isLoading.value = false;
  }
}

watch(
  currentHash,
  (hash) => {
    if (!hash) {
      previewUrl.value = '';
      loadedHash = '';
    } else if (hash !== loadedHash) {
      loadPreview(hash);
    }
  },
  { immediate: true }
);

async function ensureCompanyImagesLoaded() {
  if (companyImagesLoaded || companyImagesLoading.value) return;
  if (!props.companyId) {
    companyImagesLoaded = true;
    return;
  }
  companyImagesLoading.value = true;
  try {
    const response = await fetchCompanyImages(props.companyId);
    companyImages.value = (response.data ?? [])
      .filter((img) => !!img.hash)
      .map((img) => ({
        hash: img.hash,
        url: img.proxy?.url_thumbnail ?? img.proxy?.url_preview ?? '',
        focus: img.focus_x != null && img.focus_y != null ? { x: img.focus_x, y: img.focus_y } : undefined,
      }));
    companyImagesLoaded = true;
  } catch {
    companyImages.value = [];
  } finally {
    companyImagesLoading.value = false;
  }
}

function selectCompanyImage(img: CompanyImageThumb) {
  const option: ImageOption = { hash: img.hash };
  if (img.focus) option.focus = img.focus;
  previewUrl.value = img.url;
  loadedHash = img.hash;
  emit('update:modelValue', option);
}

function selectImage() {
  if (isUploading.value || isLoading.value) return;
  fileInput.value?.click();
}

async function uploadFile(file: File) {
  if (!file.type.includes('image/')) {
    toast.error(translator.get('publication.editor.image.invalid_file'));
    return;
  }

  const maxBytes = 10_000_000;
  if (file.size > maxBytes) {
    toast.error(translator.get('publication.editor.image.too_large'));
    return;
  }

  const formData = new FormData();
  formData.append('image', file);

  isUploading.value = true;
  try {
    const response = await requestMultipart('POST', '/v1/account/media/images/upload', { class: 0 }, formData);
    const selected = response.data as ImageObject;
    if (!selected?.hash) {
      toast.error(translator.get('publication.editor.image.missing_hash'));
      return;
    }

    const option: ImageOption = { hash: selected.hash };
    if (selected.focus_x != null && selected.focus_y != null) {
      option.focus = { x: selected.focus_x, y: selected.focus_y };
    }

    previewUrl.value = selected.proxy?.url_preview ?? selected.proxy?.url_thumbnail ?? '';
    loadedHash = option.hash;
    emit('update:modelValue', option);
  } catch (error) {
    const backendMessage = error instanceof ClientError ? error.responseJson?.message : undefined;
    toast.error(backendMessage || translator.get('publication.editor.image.upload_failed'));
  } finally {
    isUploading.value = false;
  }
}

function handleFileDrop(file: File) {
  if (isUploading.value || isLoading.value) return;
  void uploadFile(file);
}

function onFileSelected(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.files?.length) return;

  void uploadFile(target.files[0]);
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function clearImage() {
  previewUrl.value = '';
  loadedHash = '';
  emit('update:modelValue', null);
}
</script>

<style scoped>
.image-option-trigger {
  cursor: pointer;
}

.image-picker-heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.image-picker-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.image-picker-thumb {
  padding: 0;
  border: 2px solid transparent;
  border-radius: 0.25rem;
  overflow: hidden;
  background: var(--tblr-body-bg, #fff);
  aspect-ratio: 1;
  cursor: pointer;
}

.image-picker-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-picker-thumb:hover {
  border-color: var(--tblr-border-color, #e6e7e9);
}

.image-picker-thumb.selected {
  border-color: var(--tblr-primary, #206bc4);
}
</style>
